import { useRef, useCallback, useState } from 'react';
import heic2any from 'heic2any';

interface UseFileUploadProps {
  onImageCaptured: (imageDataUrl: string) => void;
  onResetCamera: () => void;
  onAnalyzeImage: () => void;
}

interface UseFileUploadReturn {
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleFileUpload: () => void;
  isConverting: boolean;
}

// Helper function to debug image data
const debugImageData = (dataUrl: string, label: string) => {
  console.log(`${label} - Length: ${dataUrl.length}`);
  console.log(`${label} - Starts with: ${dataUrl.substring(0, 50)}...`);
  
  // Check if it's a valid data URL
  const isValidDataUrl = dataUrl.startsWith('data:image/');
  console.log(`${label} - Is valid data URL: ${isValidDataUrl}`);
  
  // Extract mime type
  const mimeType = dataUrl.split(';')[0].split(':')[1];
  console.log(`${label} - Mime type: ${mimeType || 'unknown'}`);
};

export const useFileUpload = ({
  onImageCaptured,
  onResetCamera,
  onAnalyzeImage
}: UseFileUploadProps): UseFileUploadReturn => {
  const fileInputRef = useRef<HTMLInputElement>(null) as React.RefObject<HTMLInputElement>;
  const [isConverting, setIsConverting] = useState(false);

  // Handle file input change
  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      
      try {
        // Check if the file is a HEIC file
        const isHeic = file.type === 'image/heic' || 
                      file.name.toLowerCase().endsWith('.heic') || 
                      file.name.toLowerCase().endsWith('.heif');
        
        if (isHeic) {
          console.log('Converting HEIC file to JPEG...', file.name, file.type, file.size);
          setIsConverting(true);
          
          // Convert HEIC to JPEG
          const jpegBlob = await heic2any({
            blob: file,
            toType: 'image/jpeg',
            quality: 0.9 // 90% quality for better analysis
          }) as Blob;
          
          console.log('Conversion complete. JPEG size:', jpegBlob.size);
          
          // Create a new File object from the blob to maintain filename context
          const convertedFile = new File(
            [jpegBlob], 
            file.name.replace(/\.heic$|\.heif$/i, '.jpg'), 
            { type: 'image/jpeg' }
          );
          
          // Read the converted JPEG blob
          const reader = new FileReader();
          
          reader.onload = (e) => {
            if (e.target && typeof e.target.result === "string") {
              const imageDataUrl = e.target.result;
              
              // Debug the converted image data
              debugImageData(imageDataUrl, 'Converted HEIC');
              
              // Reset any previous state and set the new image
              onImageCaptured(imageDataUrl);
              onResetCamera();

              // Automatically analyze the uploaded image after a short delay
              // to ensure the image is properly loaded
              setTimeout(() => {
                setIsConverting(false);
                onAnalyzeImage();
              }, 800);
            }
          };
          
          reader.onerror = () => {
            console.error('Error reading converted file');
            setIsConverting(false);
          };
          
          reader.readAsDataURL(convertedFile);
        } else {
          // Handle regular image files
          const reader = new FileReader();
          
          reader.onload = (e) => {
            if (e.target && typeof e.target.result === "string") {
              const imageDataUrl = e.target.result;
              
              // Debug the image data
              debugImageData(imageDataUrl, 'Regular image');
              
              // Reset any previous state and set the new image
              onImageCaptured(imageDataUrl);
              onResetCamera();

              // Automatically analyze the uploaded image
              setTimeout(() => {
                onAnalyzeImage();
              }, 500);
            }
          };
          
          reader.readAsDataURL(file);
        }
      } catch (error) {
        console.error('Error processing file:', error);
        setIsConverting(false);
        alert(`Failed to process the image file. Error: ${error instanceof Error ? error.message : String(error)}`);
      }
    }

    // Reset the file input so the same file can be selected again
    if (event.target) {
      event.target.value = "";
    }
  }, [onImageCaptured, onResetCamera, onAnalyzeImage]);

  // Handle file upload button click
  const handleFileUpload = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  return {
    fileInputRef,
    handleFileChange,
    handleFileUpload,
    isConverting
  };
};
